if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.activeMediaIndex = 0;
        this.drag = null;

        this.previousButton = this.querySelector('[data-media-modal-previous]');
        this.nextButton = this.querySelector('[data-media-modal-next]');
        this.counter = this.querySelector('.product-media-modal__counter');
        this.currentCounter = this.querySelector('[data-media-modal-current]');
        this.totalCounter = this.querySelector('[data-media-modal-total]');
        this.content = this.querySelector('.product-media-modal__content');

        this.previousButton?.addEventListener('click', () => this.changeMedia(-1));
        this.nextButton?.addEventListener('click', () => this.changeMedia(1));

        [this.previousButton, this.nextButton].forEach((button) => {
          button?.addEventListener('pointerup', (event) => event.stopPropagation());
        });

        this.addEventListener('keydown', (event) => {
          if (!this.hasAttribute('open')) return;

          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.changeMedia(-1);
          }

          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.changeMedia(1);
          }
        });

        this.zoomButton = this.querySelector('[data-media-modal-zoom]');
        this.zoomButton?.addEventListener('click', () => this.toggleZoom());

        // Keep image interactions away from ModalDialog's legacy click-to-close handler.
        this.content?.addEventListener('pointerup', (event) => event.stopPropagation());
        this.zoomButton?.addEventListener('pointerup', (event) => event.stopPropagation());
        this.content?.addEventListener('click', (event) => {
          if (this.suppressClick) {
            this.suppressClick = false;
            return;
          }
          if (event.target.matches('img.active[data-media-id]')) this.toggleZoom();
        });
        this.content?.addEventListener('dragstart', (event) => {
          if (event.target.matches('img[data-media-id]')) event.preventDefault();
        });
        this.content?.addEventListener('pointerdown', (event) => {
          this.suppressClick = false;
          if (!this.zoomed || event.pointerType !== 'mouse' || event.button !== 0) return;
          this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY,
            left: this.content.scrollLeft, top: this.content.scrollTop };
          this.content.setPointerCapture(event.pointerId);
        });
        this.content?.addEventListener('pointermove', (event) => {
          if (!this.drag || this.drag.id !== event.pointerId) return;
          const dx = event.clientX - this.drag.x;
          const dy = event.clientY - this.drag.y;
          if (Math.hypot(dx, dy) > 5) this.suppressClick = true;
          this.content.scrollLeft = this.drag.left - dx;
          this.content.scrollTop = this.drag.top - dy;
        });
        ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((type) => {
          this.content?.addEventListener(type, () => { this.drag = null; });
        });
      }

      hide() {
        this.resetZoom();
        super.hide();
      }

      connectedCallback() {
        super.connectedCallback();
        if (this.preloadScheduled) return;
        this.preloadScheduled = true;
        // Let the page's visible images load first, then warm the zoom viewer.
        const warmImages = () => {
          const items = this.getMediaItems();
          this.preloadImage(items[0]);
          this.preloadImage(items[1]);
        };
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(warmImages, { timeout: 1500 });
        } else {
          window.setTimeout(warmImages, 1000);
        }
      }

      preloadImage(media, priority = 'low') {
        if (media?.tagName !== 'IMG') return;
        media.fetchPriority = priority;
        // Hidden slides must load before navigation, not only when displayed.
        media.loading = 'eager';
      }

      resetZoom() {
        this.zoomed = false;
        this.drag = null;
        this.content?.classList.remove('is-zoomed');
        this.content?.style.removeProperty('--zoom-width');
        if (this.content) this.content.scrollTop = this.content.scrollLeft = 0;
        this.zoomButton?.setAttribute('aria-pressed', 'false');
      }

      toggleZoom() {
        const image = this.content?.querySelector('img.active[data-media-id]');
        if (!image) return;
        if (this.zoomed) {
          this.resetZoom();
          return;
        }
        const width = image.getBoundingClientRect().width;
        if (!width) return;
        this.zoomed = true;
        const zoomScale = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches ? 1.6 : 2.5;
        this.content.style.setProperty('--zoom-width', `${width * 2.5}px`);
        this.content.style.setProperty('--zoom-width', `${width * zoomScale}px`);
        this.content.classList.add('is-zoomed');
        this.zoomButton?.setAttribute('aria-pressed', 'true');
        this.content.scrollLeft = (this.content.scrollWidth - this.content.clientWidth) / 2;
        this.content.scrollTop = (this.content.scrollHeight - this.content.clientHeight) / 2;
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const mediaItems = this.getMediaItems();
        const openedMediaId = this.openedBy.getAttribute('data-media-id');
        const requestedIndex = mediaItems.findIndex((element) => element.dataset.mediaId === openedMediaId);

        this.activeMediaIndex = requestedIndex >= 0 ? requestedIndex : 0;
        this.setActiveMedia(this.activeMediaIndex);
      }

      getMediaItems() {
        const mediaItems = Array.from(this.content?.querySelectorAll(':scope > [data-media-id]') || []);

        return mediaItems.filter((element, index) => {
          return !element.classList.contains('product__media-item--variant') || index === 0;
        });
      }

      changeMedia(direction) {
        const mediaItems = this.getMediaItems();
        if (mediaItems.length < 2) return;

        this.activeMediaIndex = (this.activeMediaIndex + direction + mediaItems.length) % mediaItems.length;
        window.pauseAllMedia();
        this.setActiveMedia(this.activeMediaIndex);
      }

      setActiveMedia(index) {
        this.resetZoom();
        const mediaItems = this.getMediaItems();
        const activeMedia = mediaItems[index];
        if (!activeMedia) return;

        this.preloadImage(activeMedia, 'high');
        if (mediaItems.length > 1) {
          this.preloadImage(mediaItems[(index + 1) % mediaItems.length]);
          this.preloadImage(mediaItems[(index - 1 + mediaItems.length) % mediaItems.length]);
        }

        mediaItems.forEach((element, elementIndex) => {
          const isActive = elementIndex === index;
          element.classList.toggle('active', isActive);
          element.setAttribute('aria-hidden', String(!isActive));
        });

        if (this.zoomButton) this.zoomButton.hidden = activeMedia.tagName !== 'IMG';

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        this.activeMediaIndex = index;
        if (this.currentCounter) this.currentCounter.textContent = String(index + 1);
        if (this.totalCounter) this.totalCounter.textContent = String(mediaItems.length);

        const hasMultipleMedia = mediaItems.length > 1;
        if (this.counter) this.counter.hidden = !hasMultipleMedia;
        if (this.previousButton) this.previousButton.hidden = !hasMultipleMedia;
        if (this.nextButton) this.nextButton.hidden = !hasMultipleMedia;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}